import { useQuery } from '@tanstack/react-query'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { sentryEventsApiResponseSchema, SentryEventApi, SentryIssue } from '../../types/sentryIssue.type'
import { getWorkstreamFromTransaction, getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'
import {
  isDispatchMobileProject,
  isDockAutomationProject,
  SENTRY_PROJECT_APEX_CLIENT,
  SENTRY_PROJECT_DISPATCH_MOBILE,
  SENTRY_PROJECT_DOCK_AUTOMATION,
} from '../../constants/sentryProjects'
import { TIME_THRESHOLDS, AGE_CALCULATION } from '../../constants/timeThresholds'
import { parseNumberSafe } from '../../utils/numberUtils'
import { sentryQueryKeys } from './queryKeys'

// Transform API response to UI model
const transformSentryEvent = (event: SentryEventApi, environment?: string): SentryIssue => {
  // Extract path from culprit (transaction path) - handle null and "?" cases
  const culpritValue = event.culprit && event.culprit !== '?' ? event.culprit : ''
  const path = culpritValue
  const transaction = culpritValue

  // Determine if error is handled based on isUnhandled field
  // isUnhandled: false means the error is handled
  // isUnhandled: true means the error is unhandled
  const isUnhandled =
    event.isUnhandled !== undefined
      ? event.isUnhandled
      : event.title.includes('Error') ||
        event.title.includes('Exception') ||
        event.metadata?.type?.includes('Error') ||
        false

  // Extract title and subtitle based on isUnhandled status
  const displayTitle = isUnhandled && event.metadata?.type ? event.metadata.type : event.title || 'Unknown Error'

  const displaySubtitle = isUnhandled && event.metadata?.value ? event.metadata.value : culpritValue || ''

  const subtitle =
    displaySubtitle && displaySubtitle.length > 100
      ? `${displaySubtitle.substring(0, 100)}...`
      : displaySubtitle || undefined

  // Use priority from API, or determine based on error type
  let priority: 'high' | 'medium' | 'low' = 'medium'
  if (event.priority) {
    // Handle both enum and string values
    const priorityStr = typeof event.priority === 'string' ? event.priority.toLowerCase() : event.priority
    if (priorityStr === 'high' || priorityStr === 'medium' || priorityStr === 'low') {
      priority = priorityStr
    }
  }
  // Fallback: determine priority based on error type
  if (priority === 'medium' && !event.priority) {
    if (event.title.includes('404') || event.title.includes('500')) {
      priority = 'high'
    } else if (event.title.includes('400')) {
      priority = 'medium'
    }
  }

  // Extract workstream from culprit/transaction for display purposes
  // The API already filters by workstream using culprit query pattern, so we just extract it for UI
  const workstream = getWorkstreamFromTransaction(transaction) || undefined

  // Parse event count and user count from filtered stats (for date range)
  // API Response Structure:
  // - filtered.count and filtered.userCount = Events/Users for matched filter + Time range
  // - count and userCount = Events/Users for Time range (24h stats)
  // - lifetime.count and lifetime.userCount = Total events/users across all time

  // Use filtered counts (for the selected date range), fallback to count, then lifetime
  // Priority: filtered.count (for date range) > count (24h) > lifetime.count (all time)
  const totalEvents =
    parseNumberSafe(event.filtered?.count) ||
    parseNumberSafe(event.count) ||
    parseNumberSafe(event.lifetime?.count) ||
    0

  const affectedUsers =
    parseNumberSafe(event.filtered?.userCount) ||
    parseNumberSafe(event.userCount) ||
    parseNumberSafe(event.lifetime?.userCount) ||
    0

  // Calculate age from firstSeen using Unix time approach (matches Sentry backend logic)
  // Priority: lifetime.firstSeen (all time - when issue was first created) > filtered.firstSeen > firstSeen (24h)
  let age = 'Unknown'
  const firstSeenValue = event.lifetime?.firstSeen || event.filtered?.firstSeen || event.firstSeen
  if (firstSeenValue) {
    try {
      const firstSeenDate = new Date(firstSeenValue)

      // ✅ Validate date is valid before using
      if (isNaN(firstSeenDate.getTime())) {
        console.warn('Invalid date for firstSeen:', firstSeenValue)
        age = 'Unknown'
      } else {
        const now = new Date()

        // Calculate time difference in seconds (like Sentry backend)
        const totalSeconds = Math.floor((now.getTime() - firstSeenDate.getTime()) / 1000)

        // ✅ Additional safety checks for edge cases
        if (totalSeconds < 0 || !isFinite(totalSeconds)) {
          console.warn('Invalid totalSeconds calculated:', { totalSeconds, firstSeenValue })
          age = 'Unknown'
        } else {
          // Display in most appropriate unit based on actual time thresholds
          if (totalSeconds >= TIME_THRESHOLDS.YEAR) {
            // >= 1 year (365 days)
            const totalYears = Math.round(totalSeconds / TIME_THRESHOLDS.YEAR)
            age = totalYears === 1 ? '1 year' : `${totalYears} years`
          } else if (totalSeconds >= TIME_THRESHOLDS.MONTH) {
            // >= 30 days (1 month)
            // Sentry uses conditional rounding: round for <6mo, truncate for >=6mo
            const monthsRaw = totalSeconds / TIME_THRESHOLDS.MONTH
            const totalMonths =
              monthsRaw >= AGE_CALCULATION.MONTH_ROUNDING_THRESHOLD ? Math.floor(monthsRaw) : Math.round(monthsRaw)
            age = totalMonths === 1 ? '1 month' : `${totalMonths} months`
          } else if (totalSeconds >= TIME_THRESHOLDS.WEEK) {
            // >= 7 days (1 week)
            const totalWeeks = Math.round(totalSeconds / TIME_THRESHOLDS.WEEK)
            age = totalWeeks === 1 ? '1 week' : `${totalWeeks} weeks`
          } else if (totalSeconds >= TIME_THRESHOLDS.DAY) {
            // >= 24 hours (1 day)
            const totalDays = Math.round(totalSeconds / TIME_THRESHOLDS.DAY)
            age = totalDays === 1 ? '1 day' : `${totalDays} days`
          } else if (totalSeconds >= TIME_THRESHOLDS.HOUR) {
            // >= 1 hour
            const totalHours = Math.round(totalSeconds / TIME_THRESHOLDS.HOUR)
            age = totalHours === 1 ? '1 hour' : `${totalHours} hours`
          } else {
            // < 1 hour
            const totalMinutes = Math.floor(totalSeconds / TIME_THRESHOLDS.MINUTE)
            age = totalMinutes === 1 ? '1 minute' : `${totalMinutes} minutes`
          }
        }
      }
    } catch (error) {
      console.warn('Error calculating age:', error, { firstSeenValue })
      age = 'Unknown'
    }
  }

  // Store last seen ISO timestamp string for formatting
  // Use lifetime.lastSeen as per requirement - show exact time from API without conversion
  let lastSeen: string | undefined
  const lastSeenValue = event.lifetime?.lastSeen
  if (lastSeenValue) {
    // Store the ISO timestamp string directly for formatting (no conversion to local time)
    lastSeen = typeof lastSeenValue === 'string' ? lastSeenValue : new Date(lastSeenValue).toISOString()
  }

  // Map substatus to trend
  let trend: 'ongoing' | 'escalating' | 'resolved' | 'new' = 'ongoing'
  if (event.substatus) {
    const substatusLower = event.substatus.toLowerCase()
    if (substatusLower === 'escalating') trend = 'escalating'
    else if (substatusLower === 'ongoing') trend = 'ongoing'
    else if (substatusLower === 'resolved' || substatusLower === 'archived') trend = 'resolved'
    else if (substatusLower === 'new') trend = 'new'
  }

  return {
    id: event.id,
    title: displayTitle,
    subtitle,
    type: event.type || event.issueType || 'error',
    status: event.status || 'unresolved',
    level: event.level || 'error',
    project: event.project.name,
    enrichedEnvironment: (event.enrichedEnvironment || environment) as string | undefined, // Use enrichedEnvironment from API, fallback to filter parameter
    lastSeen: lastSeen || 'Just now', // ISO timestamp string or fallback
    lastSeenTimestamp: lastSeen ? new Date(lastSeen) : undefined,
    age,
    trend,
    trendValue: Math.floor(Math.random() * 100), // Mock value - could be derived from count trends
    events24h: 0, // Mock value - would need separate API call
    events14d: 0, // Mock value - would need separate API call
    totalEvents,
    affectedUsers,
    priority,
    path,
    transaction,
    workstream,
    isUnhandled,
    logger: event.logger || '',
    shortId: event.shortId,
  }
}

export interface SentryEventsResponse {
  filteredCount?: number
  issues: SentryIssue[]
  pagination?: {
    nextCursor?: string
    hasMore?: boolean
    recordCount?: number
  }
  'x-hits'?: number // Total count from API response
}

export const useSentryEvents = (
  environment?: string,
  startDate?: Date | null,
  endDate?: Date | null,
  nextCursor?: string | null,
  workstream?: string | null,
  searchQuery?: string,
  projectId?: string
) => {
  const effectiveProjectId =
    projectId ??
    (workstream === 'dispatch-mobile'
      ? SENTRY_PROJECT_DISPATCH_MOBILE
      : workstream === 'dock-automation'
        ? SENTRY_PROJECT_DOCK_AUTOMATION
        : SENTRY_PROJECT_APEX_CLIENT)
  const isDispatchMobile = isDispatchMobileProject(effectiveProjectId)
  const isDockAutomation = isDockAutomationProject(effectiveProjectId)

  const startDateKey = startDate ? startDate.toISOString() : null
  const endDateKey = endDate ? endDate.toISOString() : null

  return useQuery({
    queryKey: sentryQueryKeys.events.list({
      projectId: effectiveProjectId,
      environment,
      startDate: startDateKey,
      endDate: endDateKey,
      workstream,
      searchQuery,
      cursor: nextCursor,
    }),
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      queryParams.append('expand', 'owners')
      queryParams.append('expand', 'inbox')
      queryParams.append('project', effectiveProjectId)

      // Always send query parameter for consistency with priority counts and severity percentage APIs
      if (workstream === null || isDispatchMobile || isDockAutomation) {
        // Overview page / Dispatch Mobile / Dock Automation
        if (searchQuery && searchQuery.trim()) {
          queryParams.append('query', `title:"${searchQuery.trim()}"`)
        } else {
          // Send empty query parameter
          queryParams.append('query', '')
        }
      } else {
        // Apex Client with workstream filter
        const workstreamQuery = getWorkstreamCulpritQuery(workstream)
        let finalQuery = workstreamQuery || 'culprit:"*/linehaul/*"'
        if (searchQuery && searchQuery.trim()) {
          finalQuery = `${finalQuery} title:"${searchQuery.trim()}"`
        }
        queryParams.append('query', finalQuery)
      }

      // Add environment parameter based on filter
      // If a specific environment is selected (not "all"), use environment parameter
      // Otherwise, use includeEnvironment: true
      if (environment && environment !== 'all') {
        queryParams.append('environment', environment)
      } else {
        queryParams.append('includeEnvironment', 'true')
      }

      // Check if date range is default 24 hours (yesterday to today)
      // Only use statsPeriod=24h for the default range, not for single date selections
      const isSameDate =
        startDate &&
        endDate &&
        startDate.getFullYear() === endDate.getFullYear() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getDate() === endDate.getDate()

      const is24HoursRange =
        startDate && endDate && !isSameDate && Math.abs(endDate.getTime() - startDate.getTime()) <= 24 * 60 * 60 * 1000

      // Add statsPeriod only for yesterday-to-today range, otherwise use explicit start/end dates
      if (is24HoursRange) {
        queryParams.append('statsPeriod', '24h')
      } else if (startDate && endDate) {
        // Format dates: start at 00:00:00, end at 23:59:59
        const year = startDate.getFullYear()
        const month = String(startDate.getMonth() + 1).padStart(2, '0')
        const day = String(startDate.getDate()).padStart(2, '0')
        const startDateString = `${year}-${month}-${day}T00:00:00`
        queryParams.append('start', startDateString)

        const endYear = endDate.getFullYear()
        const endMonth = String(endDate.getMonth() + 1).padStart(2, '0')
        const endDay = String(endDate.getDate()).padStart(2, '0')
        const endDateString = `${endYear}-${endMonth}-${endDay}T23:59:59`
        queryParams.append('end', endDateString)
      }

      // Add cursor for pagination (API expects 'cursor' parameter, not 'nextCursor')
      if (nextCursor) {
        queryParams.append('cursor', nextCursor)
      }

      // Replace + with %20 in the query string for proper space encoding
      const queryString = queryParams.toString().replace(/\+/g, '%20')

      const hasSearch = searchQuery && searchQuery.trim()
      const baseEndpoint = hasSearch ? apiUrls.searchSentryIssues : apiUrls.getSentryIssues
      const apiEndpoint = `${baseEndpoint}?${queryString}`
      const response = await http.get(apiEndpoint)
      const data = response.data // Extract data from axios response

      // Log the raw response for debugging, including x-hits
      // Parse and validate the response
      let parsed
      try {
        parsed = sentryEventsApiResponseSchema.parse(data)
      } catch (parseError: unknown) {
        // Try to handle gracefully - check for data array in various possible locations
        let issues: unknown[] = []

        if (data && typeof data === 'object') {
          // Check if data is directly an array
          if (Array.isArray(data)) {
            issues = data
          }
          // Check if data.data exists and is an array
          else if ('data' in data && Array.isArray((data as Record<string, unknown>).data)) {
            issues = (data as Record<string, unknown>).data as unknown[]
          }
          // Check if data.issues exists and is an array (alternative structure)
          else if ('issues' in data && Array.isArray((data as Record<string, unknown>).issues)) {
            issues = (data as Record<string, unknown>).issues as unknown[]
          }
          // Check if data.items exists and is an array (another alternative)
          else if ('items' in data && Array.isArray((data as Record<string, unknown>).items)) {
            issues = (data as Record<string, unknown>).items as unknown[]
          }
        }

        if (issues.length > 0) {
          const transformed: (SentryIssue | null)[] = issues.map((issue: unknown) => {
            try {
              return transformSentryEvent(issue as SentryEventApi, environment)
            } catch {
              return null
            }
          })
          const validIssues = transformed.filter((issue): issue is SentryIssue => issue !== null)
          // Extract x-hits from lenient parsing path
          const xHitsFromHeaders =
            (response.headers?.['x-hits'] && parseInt(String(response.headers['x-hits']), 10)) || undefined

          const dataRecord = data as Record<string, unknown>
          const xHitsFromBody = (dataRecord?.['x-hits'] && parseInt(String(dataRecord?.['x-hits']), 10)) || undefined

          const xHitsValue =
            xHitsFromBody || xHitsFromHeaders || dataRecord.recordCount || dataRecord.totalCount || validIssues.length

          return {
            issues: validIssues,
            pagination: {
              nextCursor: (dataRecord.pagination as Record<string, unknown> | undefined)?.nextCursor as
                | string
                | null
                | undefined,
              hasMore: (dataRecord.pagination as Record<string, unknown> | undefined)?.hasMore as boolean | undefined,
              recordCount:
                (dataRecord.recordCount as number | undefined) ||
                (dataRecord.totalCount as number | undefined) ||
                validIssues.length,
            },
            'x-hits': xHitsValue,
            categoryCounts: dataRecord.categoryCounts,
          } as SentryEventsResponse
        }

        throw parseError
      }

      // Check if parsed.data exists and is an array
      let issuesArray: SentryEventApi[] = []
      const parsedRecord = parsed as Record<string, unknown>
      // First, check if parsed.data exists and is an array
      if (parsed.data && Array.isArray(parsed.data) && parsed.data.length > 0) {
        issuesArray = parsed.data
      }
      // Try to find issues in alternative locations
      else if (Array.isArray(parsedRecord.issues) && parsedRecord.issues.length > 0) {
        issuesArray = parsedRecord.issues as SentryEventApi[]
      } else if (Array.isArray(parsedRecord.items) && parsedRecord.items.length > 0) {
        issuesArray = parsedRecord.items as SentryEventApi[]
      } else if (Array.isArray(parsedRecord.results) && parsedRecord.results.length > 0) {
        issuesArray = parsedRecord.results as SentryEventApi[]
      }
      // Check if the response itself is an array (unlikely but possible)
      else if (Array.isArray(data) && data.length > 0) {
        issuesArray = data as SentryEventApi[]
      }
      // If we still don't have issues, check if maybe the response structure is completely different
      else {
        return {
          issues: [],
          pagination: {
            nextCursor: parsed.pagination?.nextCursor,
            hasMore: parsed.pagination?.hasMore,
            recordCount: parsed.recordCount || parsed.totalCount || 0,
          },
        } as SentryEventsResponse
      }

      // Transform API events to UI model
      // Pass environment parameter to transformation
      const transformed = issuesArray
        .map((event) => {
          try {
            const transformedIssue = transformSentryEvent(event, environment)
            return transformedIssue
          } catch {
            return null
          }
        })
        .filter((issue): issue is SentryIssue => issue !== null)

      const xHitsFromHeaders =
        (response.headers?.['x-hits'] && parseInt(String(response.headers['x-hits']), 10)) || undefined

      // Check all possible body locations
      const dataRecord = data as Record<string, unknown>
      const xHitsFromBody = dataRecord?.['x-hits']

      // Prioritize body over headers for this API
      const xHitsValue =
        xHitsFromBody ??
        xHitsFromHeaders ??
        parsed['x-hits'] ??
        parsed.recordCount ??
        parsed.totalCount ??
        transformed.length // Last resort fallback

      const filteredCountValue = parsed.filteredCount ?? dataRecord?.['filteredCount']
      return {
        issues: transformed,
        pagination: {
          nextCursor: parsed.pagination?.nextCursor,
          hasMore: parsed.pagination?.hasMore,
          recordCount: parsed.recordCount || parsed.totalCount,
        },
        'x-hits': xHitsValue,
        filteredCount: filteredCountValue,
      } as SentryEventsResponse
    },
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401 errors (authentication issues)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } }
        if (axiosError.response?.status === 401) {
          return false
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2
    },
    // Only enable query if user is authenticated (optional - remove if API doesn't require auth)
    // enabled: isAuthenticated,
  })
}
